import { FC, useContext, useEffect, useRef, useState } from "react";

import { WebsocketStatusType } from "../../shared/types/pubsub";
import { AUTH_ERRORS } from "../../shared/types/websocket-auth";

import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Tooltip } from "mds/components/Tooltip";
import {
  CloudConnectedIcon,
  CloudConnectingIcon,
  CloudDisconnectedIcon,
  RefreshIcon,
} from "mds/icons";
import { IconProps } from "mds/utils/images";
import { PubsubWebsocketContext } from "providers/PubsubWebsocketProvider";
import { ShareDBContext } from "providers/ShareDBProvider";
import { useAppSelector } from "store/index";
import { selectShareDBOpStatus } from "store/selectors";
import { getCombinedConnectionStatus } from "utils/connection-status";
import { isLocalEnv } from "utils/env";

// After this threshold the connection is considered delayed and an
// error banner is shown to the user.
const SHAREDB_DELAY_THRESHOLD_MS = 5000;

type ConnectionStateType = "connected" | "connecting" | "disconnected" | "saving";

/**
 * Active states for the saving status of a document / sharedb operation.
 * null indicates that the document is saved and no special status should be shown.
 */

type NonNullDocSavingStatusType = "delayed" | "saving";
type DocSavingStatusType = NonNullDocSavingStatusType | null;

const SAVING_STATUS: Record<string, DocSavingStatusType> = {
  DELAYED: "delayed",
  SAVING: "saving",
};

const WEBSOCKET_STATE_TO_ICON_STATE: Record<
  WebsocketStatusType | NonNullDocSavingStatusType,
  ConnectionStateType
> = {
  connected: "connected",
  delayed: "connecting",
  // Change the indicator for integration tests so we don't have flakey tests due to the rotating icon.
  saving: isLocalEnv ? "connected" : "saving",
  connecting: isLocalEnv ? "connected" : "connecting",
  disconnected: "disconnected",
  stopped: "disconnected",
  closed: "disconnected",
  "401": "disconnected",
  failed: "disconnected",
};

const ICON_STATE_TO_COLOR: Record<ConnectionStateType, string> = {
  connecting: "text-orange",
  connected: "text-green",
  disconnected: "text-red",
  saving: "text-blue",
};

const ICON_STATE_TO_ICON_COMPONENT: Record<ConnectionStateType, FC<IconProps>> = {
  saving: RefreshIcon,
  connecting: CloudConnectingIcon,
  connected: CloudConnectedIcon,
  disconnected: CloudDisconnectedIcon,
};

const ICON_STATE_TO_TOOLTIP: Record<ConnectionStateType, string> = {
  connected: t("websocket.saving_indicator.connected"),
  connecting: t("websocket.saving_indicator.connecting"),
  disconnected: t("websocket.saving_indicator.disconnected"),
  saving: t("websocket.saving_indicator.saving"),
};

type ConnectionIconProps = {
  iconState: ConnectionStateType;
  classOverride?: string;
  showText?: boolean;
};

export const ConnectionIcon = ({ iconState, classOverride, showText }: ConnectionIconProps) => {
  const Icon = ICON_STATE_TO_ICON_COMPONENT[iconState];
  const colorClass = ICON_STATE_TO_COLOR[iconState];
  const tooltipMessage = ICON_STATE_TO_TOOLTIP[iconState];
  const ariaLabel = t("websocket.saving_indicator.aria_label", { status: tooltipMessage });

  return (
    <Tooltip text={!showText ? tooltipMessage : undefined}>
      <div className={iconState === "saving" ? "animate-rotate-clockwise" : undefined}>
        <IconText
          iconStart={
            <Icon aria-label={ariaLabel} className={classOverride || colorClass} size="l" />
          }
          text={showText ? tooltipMessage : undefined}
        />
      </div>
    </Tooltip>
  );
};

type ConnectionStatusIndicatorProps = {
  className?: string;
  iconColorClass?: string;
  showText?: boolean;
};

/**
 * Represents both the current connection status to the websocket/server,
 * and also displays saving status indication for any document currently open.
 *
 * Connection errors are surfaced via the full-width connection banner in GlobalBanners.tsx.
 * This indicator only shows the icon state in the header.
 */
const getOverallConnectionStatus = (
  shareDBStatus: WebsocketStatusType | null,
  pubsubStatus: WebsocketStatusType,
  savingStatus: DocSavingStatusType,
  opError: string | null,
  isOverwriteError: boolean,
): WebsocketStatusType | NonNullDocSavingStatusType => {
  // Op errors take highest priority (except overwrite errors)
  if (opError && !isOverwriteError) {
    return "disconnected";
  }

  // Saving status takes priority over connection status
  if (savingStatus) {
    return savingStatus;
  }

  return getCombinedConnectionStatus(shareDBStatus, pubsubStatus);
};

export const ConnectionStatusIndicator: FC<ConnectionStatusIndicatorProps> = ({
  className,
  iconColorClass,
  showText,
}) => {
  const shareDBContext = useContext(ShareDBContext);
  const pubsubContext = useContext(PubsubWebsocketContext);
  // ShareDB may not be available on pages like home page (only mounted in section routes)
  const shareDBStatus = shareDBContext?.shareDBStatus ?? null;
  const pubsubStatus = pubsubContext?.pubsubStatus ?? "connecting";
  const [savingStatus, setSavingStatus] = useState<DocSavingStatusType>(null);
  const { lastOpAt, lastOpSavedAt, opError } = useAppSelector(selectShareDBOpStatus);
  const delayTimeout = useRef<NodeJS.Timeout>(null);
  const minUiFlickerTimeout = useRef<NodeJS.Timeout>(null);
  const isOverwriteError = opError === AUTH_ERRORS.OVERWRITE_DISALLOWED;

  const connectionIndicatorStatus = getOverallConnectionStatus(
    shareDBStatus,
    pubsubStatus,
    savingStatus,
    opError,
    isOverwriteError,
  );
  const iconState = WEBSOCKET_STATE_TO_ICON_STATE[connectionIndicatorStatus];

  useEffect(() => {
    if (!lastOpAt) {
      return;
    }
    if (delayTimeout.current) {
      clearTimeout(delayTimeout.current);
    }
    if (minUiFlickerTimeout.current) {
      clearTimeout(minUiFlickerTimeout.current);
    }
    if (lastOpSavedAt && lastOpAt < lastOpSavedAt) {
      minUiFlickerTimeout.current = setTimeout(() => {
        setSavingStatus(null);
      }, 1000);
    } else {
      setSavingStatus((status) => status || SAVING_STATUS.SAVING);
      delayTimeout.current = setTimeout(() => {
        setSavingStatus(SAVING_STATUS.DELAYED);
      }, SHAREDB_DELAY_THRESHOLD_MS);
    }
  }, [lastOpSavedAt, lastOpAt]);

  useEffect(() => {
    if (iconState !== "disconnected" || isOverwriteError) {
      return;
    }
    setSavingStatus(null);
  }, [opError, iconState, isOverwriteError]);

  return (
    <div className={className}>
      <ConnectionIcon classOverride={iconColorClass} iconState={iconState} showText={showText} />
    </div>
  );
};
